/**
 * 환경변수 기반 로깅 유틸리티
 * 개발 환경에서만 디버그 로그를 출력하고, 프로덕션에서는 억제합니다.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export class Logger {
  private static instance: Logger;
  private isDevelopment: boolean;
  private isDebugEnabled: boolean;

  private constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isDebugEnabled = process.env.BILLING_DEBUG === 'true' || this.isDevelopment;
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  /**
   * 디버그 로그 출력 (개발 환경 또는 BILLING_DEBUG=true일 때만)
   */
  debug(message: string, data?: any): void {
    if (this.isDebugEnabled) {
      const timestamp = new Date().toISOString();
      if (data !== undefined) {
        console.log(`[${timestamp}] [BILLING_DEBUG] ${message}`, data);
      } else {
        console.log(`[${timestamp}] [BILLING_DEBUG] ${message}`);
      }
    }
  }

  /**
   * 정보 로그 출력
   */
  info(message: string, data?: any): void {
    const timestamp = new Date().toISOString();
    if (data !== undefined) {
      console.log(`[${timestamp}] [BILLING_INFO] ${message}`, data);
    } else {
      console.log(`[${timestamp}] [BILLING_INFO] ${message}`);
    }
  }

  /**
   * 경고 로그 출력
   */
  warn(message: string, data?: any): void {
    const timestamp = new Date().toISOString();
    if (data !== undefined) {
      console.warn(`[${timestamp}] [BILLING_WARN] ${message}`, data);
    } else {
      console.warn(`[${timestamp}] [BILLING_WARN] ${message}`);
    }
  }

  /**
   * 에러 로그 출력
   */
  error(message: string, error?: any): void {
    const timestamp = new Date().toISOString();
    if (error !== undefined) {
      console.error(`[${timestamp}] [BILLING_ERROR] ${message}`, error);
    } else {
      console.error(`[${timestamp}] [BILLING_ERROR] ${message}`);
    }
  }

  /**
   * 성능 측정을 위한 타이머 시작
   */
  startTimer(label: string): void {
    if (this.isDebugEnabled) {
      console.time(`[BILLING_TIMER] ${label}`);
    }
  }

  /**
   * 성능 측정을 위한 타이머 종료
   */
  endTimer(label: string): void {
    if (this.isDebugEnabled) {
      console.timeEnd(`[BILLING_TIMER] ${label}`);
    }
  }

  /**
   * 디버그 모드 여부 확인
   */
  isDebugMode(): boolean {
    return this.isDebugEnabled;
  }
}

// 싱글톤 인스턴스 내보내기
export const logger = Logger.getInstance();

// 편의를 위한 개별 함수들 내보내기
export const debugLog = (message: string, data?: any) => logger.debug(message, data);
export const infoLog = (message: string, data?: any) => logger.info(message, data);
export const warnLog = (message: string, data?: any) => logger.warn(message, data);
export const errorLog = (message: string, error?: any) => logger.error(message, error);
